import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { TestimonialCard } from "@/components/ui/testimonial-card";
import { cn } from "@/lib/utils";

export function TestimonialsWithMarquee() {
  const { t, i18n } = useTranslation("home");
  const isRTL = i18n.dir() === "rtl";
  
  const testimonials = t("testimonials.items", { returnObjects: true }) as Array<{
    quote: string;
    author: string;
    role: string;
    industry: string;
  }>;
  
  const stats = t("testimonials.stats", { returnObjects: true }) as Array<{
    value: string;
    label: string;
  }>;
  
  const badge = t("testimonials.badge");
  const title = t("testimonials.title");
  const titleAccent = t("testimonials.titleAccent");
  const subtitle = t("testimonials.subtitle");

  return (
    <section className="py-16 sm:py-24 overflow-hidden bg-gradient-to-b from-secondary/30 to-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-4 py-1.5 text-sm font-medium text-accent"
          >
            {badge}
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            {title}{" "}
            <span className="text-accent">{titleAccent}</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground max-w-2xl text-base sm:text-lg"
          >
            {subtitle}
          </motion.p>
        </div>

        {/* Marquee Container */}
        <div className="relative">
          {/* Fade masks */}
          <div className="pointer-events-none absolute inset-y-0 start-0 w-16 sm:w-32 bg-gradient-to-e from-background to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 end-0 w-16 sm:w-32 bg-gradient-to-s from-background to-transparent z-10" 
               style={{ 
                 background: isRTL 
                   ? 'linear-gradient(to left, transparent, hsl(var(--background)))' 
                   : 'linear-gradient(to right, transparent, hsl(var(--background)))'
               }} 
          />
          <div className="pointer-events-none absolute inset-y-0 start-0 w-16 sm:w-32 z-10" 
               style={{ 
                 background: isRTL 
                   ? 'linear-gradient(to right, transparent, hsl(var(--background)))' 
                   : 'linear-gradient(to left, transparent, hsl(var(--background)))'
               }} 
          />

          {/* Marquee */}
          <div 
            className="flex gap-4 sm:gap-6 overflow-hidden py-4 group"
            style={{ '--gap': '1.5rem', '--duration': '40s' } as React.CSSProperties}
          >
            <motion.div
              className={cn(
                "flex shrink-0 gap-4 sm:gap-6",
                "group-hover:[animation-play-state:paused]"
              )}
              animate={{
                x: isRTL ? ["0%", "50%"] : ["0%", "-50%"]
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 40,
                  ease: "linear",
                },
              }}
            >
              {/* Duplicate testimonials for seamless loop */}
              {[...testimonials, ...testimonials, ...testimonials, ...testimonials].map((testimonial, i) => (
                <TestimonialCard
                  key={i}
                  author={{
                    name: testimonial.author,
                    role: testimonial.role,
                    industry: testimonial.industry,
                  }}
                  text={testimonial.quote}
                  className="w-[280px] sm:w-[340px] md:w-[380px] shrink-0"
                />
              ))}
            </motion.div>
          </div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 sm:mt-16"
        >
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12 lg:gap-16">
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center gap-1 text-center">
                <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-accent">
                  {stat.value}
                </span>
                <span className="text-sm sm:text-base text-muted-foreground">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
